import React, { useEffect, useState } from 'react'
import { FileType } from '@repo/types/common'
import { VideoIcon } from '@radix-ui/react-icons'
import { useEditor } from '../hooks'
import { MarkButton } from '../leaf/mark-button'
import { LinkMark } from '../link'
import { ButtonMark } from '../button-custom'
import { EditorInput } from '../editor-input'
import { IconProvider } from '../../icons/icon-provider'
import { MarkUploadImage } from '../link/mark-upload-image'
import { BlockButton } from '../node/mark-block'
import { BlockList } from '../node/mark-list'
import { AlphaList, BulletList, NumberList } from '../../../icons'
import '../editor-view.css'

export type EditorToolbarProps = {
  withTypo?: boolean
  withLink?: boolean
  withUploadImage?: {
    userId: number
    fileType: FileType
  }
  withVideo?: boolean
  withBulletList?: boolean
  withButton?: boolean
  withHeading?: boolean
  /**
   * Mantiene la barra visible al hacer scroll dentro de textos largos (#2416).
   * Es opt-in a propósito: el issue deja fuera el editor de comentarios de apps/web.
   */
  sticky?: boolean
}
export type EditorInputProps = {
  placeholder: string
  onValueChange: (value: any[]) => void
}

export type EditorProps = {
  inputConfig: EditorInputProps
  toolbarConfig: EditorToolbarProps
  /** Añade el conmutador Editar / Vista previa sobre la barra de herramientas. */
  withPreview?: boolean
}

export function Editor({ inputConfig, toolbarConfig, withPreview }: EditorProps) {
  const { getRawValue } = useEditor()
  const [preview, setPreview] = useState(false)

  useEffect(() => {
    inputConfig.onValueChange(getRawValue())
  }, [getRawValue])

  // Conmutador y barra van en una MISMA cabecera sticky: si cada uno llevara su
  // propio `top-0` se solaparían al hacer scroll.
  const header = (
    <>
      {withPreview && <PreviewToggle preview={preview} onChange={setPreview} />}
      {!preview && <EditorToolbar {...toolbarConfig} sticky={false} />}
    </>
  )

  return (
    <>
      {toolbarConfig.sticky ? (
        <div className="sticky top-0 z-10 shadow-sm">{header}</div>
      ) : (
        header
      )}

      {preview ? (
        // El mismo render que verá el usuario final: EditorInput en readOnly es
        // exactamente lo que monta el viewer de apps/web.
        <div className="w-full p-3 text-[14px]">
          <EditorInput readOnly onKeyDown={() => {}} placeholder="" />
        </div>
      ) : (
        <div className="flex w-full flex-col gap-2">
          <div className="w-full p-3 text-[14px] text-[#67727E]">
            <EditorInput
              onKeyDown={() => {}}
              placeholder={inputConfig.placeholder}
            />
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Conmutador Editar / Vista previa. Útil sobre todo al pegar una tabla markdown:
 * permite comprobar cómo queda sin los artefactos de edición.
 */
export function PreviewToggle({
  preview,
  onChange,
}: {
  preview: boolean
  onChange: (value: boolean) => void
}) {
  const base = 'rounded-[5px] px-3 py-1 text-[12px] font-medium transition-colors'
  return (
    <div className="flex flex-row gap-1 border-b border-[#0000001a] bg-gray-100 p-1">
      <button
        type="button"
        aria-pressed={!preview}
        onClick={() => onChange(false)}
        className={`${base} ${!preview ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`}>
        Editar
      </button>
      <button
        type="button"
        aria-pressed={preview}
        onClick={() => onChange(true)}
        className={`${base} ${preview ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`}>
        Vista previa
      </button>
    </div>
  )
}

export function EditorToolbar(config: EditorToolbarProps) {
  return (
    <div
      className={
        config.sticky
          ? 'editor-toolbar sticky top-0 z-10 border-b border-[#0000001a] shadow-sm'
          : 'editor-toolbar'
      }>
      {config.withTypo && (
        <>
          <div className={'flex flex-row gap-1'}>
            <MarkButton format={'bold'}>B</MarkButton>
            <MarkButton className="pr-1" format="italic">
              <i>I</i>
            </MarkButton>
            <MarkButton format="underline">
              <span style={{ textDecoration: 'underline' }}>U</span>
            </MarkButton>
          </div>

          <Separator />
        </>
      )}

      {config.withHeading && (
        <>
          <div className={'flex flex-row gap-2'}>
            <BlockButton className={'w-10'} format="heading-one">
              H1
            </BlockButton>
            <BlockButton format="heading-two">H2</BlockButton>
          </div>
          <Separator />
        </>
      )}

      {config.withBulletList && (
        <>
          <div className={'flex flex-row gap-2 opacity-70'}>
            <BlockList format={'bulleted-list'}>
              <BulletList className={'h-[20px] w-[20px]'} />
            </BlockList>
            <BlockList format={'numbered-list'}>
              <NumberList className={'h-[20px] w-[20px]'} />
            </BlockList>
            <BlockList format={'alpha-list'}>
              <AlphaList className={'h-[20px] w-[20px]'} />
            </BlockList>
          </div>
          <Separator />
        </>
      )}

      {(config.withUploadImage || config.withVideo) && (
        <>
          <div className={'flex flex-row gap-2 opacity-70'}>
            {config.withVideo && (
              <LinkMark format={'video-custom'} dialogTitle={'Adjuntar vídeo'}>
                <VideoIcon />
              </LinkMark>
            )}

            {config.withUploadImage && (
              <MarkUploadImage
                fileType={config.withUploadImage.fileType}
                dialogTitle={'Añadir imagen'}
                userId={config.withUploadImage.userId || 0}
              />
            )}
          </div>
          <Separator />
        </>
      )}

      {(config.withButton || config.withLink) && (
        <>
          <div className={'flex flex-row gap-2 opacity-70'}>
            {config.withLink && (
              <LinkMark format={'link'} dialogTitle={'Añadir link'}>
                <IconProvider width={20} title="IoLink" />
              </LinkMark>
            )}
            {config.withButton && (
              <ButtonMark format={'button-custom'} className="mx-0" />
            )}
          </div>
          <Separator />
        </>
      )}
    </div>
  )
}

function Separator() {
  return <div className={'w-4'} />
}
